<template>
  <div class="qrcode-tool">
    <h1>二维码生成器</h1>
    
    <div class="generator-container">
      <!-- 输入区域 -->
      <div class="input-section">
        <el-input
          v-model="content"
          type="textarea"
          :rows="4"
          placeholder="请输入要生成二维码的内容"
          @input="generateQR"
        />
        
        <!-- 选项设置 -->
        <div class="options">
          <div class="option-item">
            <span>尺寸：</span>
            <el-slider
              v-model="size"
              :min="100"
              :max="400"
              :step="10"
              @change="generateQR"
            />
          </div>
          
          <div class="option-item">
            <span>容错级别：</span>
            <el-radio-group v-model="errorLevel" @change="generateQR">
              <el-radio-button label="L">低</el-radio-button>
              <el-radio-button label="M">中</el-radio-button>
              <el-radio-button label="Q">较高</el-radio-button>
              <el-radio-button label="H">高</el-radio-button>
            </el-radio-group>
          </div>
          
          <div class="option-item">
            <span>深色：</span>
            <el-color-picker v-model="darkColor" @change="generateQR" />
            <span class="ml-20">浅色：</span>
            <el-color-picker v-model="lightColor" @change="generateQR" />
          </div>
        </div>
      </div>

      <!-- 预览区域 -->
      <div class="preview-section" v-loading="loading">
        <div class="qrcode-wrapper" ref="qrcodeRef"></div>
        <div class="button-group">
          <el-button type="primary" @click="downloadQR">下载二维码</el-button>
        </div>
      </div>
    </div>

    <!-- 说明区域 -->
    <div class="info-section">
      <h3>说明</h3>
      <ul>
        <li>支持文本、网址、联系方式等内容生成二维码</li>
        <li>容错级别越高，二维码图案越复杂，但抗干扰能力越强</li>
        <li>可自定义二维码颜色和尺寸</li>
        <li>生成的二维码图片为PNG格式</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import QRCode from 'qrcode'

const content = ref('')
const size = ref(200)
const errorLevel = ref('M')
const darkColor = ref('#000000')
const lightColor = ref('#FFFFFF')
const loading = ref(false)
const qrcodeRef = ref(null)

const generateQR = async () => {
  if (!content.value) {
    if (qrcodeRef.value) {
      qrcodeRef.value.innerHTML = ''
    }
    return
  }

  loading.value = true
  try {
    const canvas = await QRCode.toCanvas(content.value, {
      width: size.value,
      margin: 1,
      color: {
        dark: darkColor.value,
        light: lightColor.value
      },
      errorCorrectionLevel: errorLevel.value
    })

    if (qrcodeRef.value) {
      qrcodeRef.value.innerHTML = ''
      qrcodeRef.value.appendChild(canvas)
    }
  } catch (error) {
    ElMessage.error('生成二维码失败：' + error.message)
  } finally {
    loading.value = false
  }
}

const downloadQR = () => {
  if (!content.value) {
    ElMessage.warning('请先输入内容')
    return
  }

  const canvas = qrcodeRef.value?.querySelector('canvas')
  if (!canvas) {
    ElMessage.error('二维码未生成')
    return
  }

  try {
    const link = document.createElement('a')
    link.download = 'qrcode.png'
    link.href = canvas.toDataURL('image/png')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    ElMessage.success('下载成功')
  } catch (error) {
    ElMessage.error('下载失败：' + error.message)
  }
}

onMounted(() => {
  if (content.value) {
    generateQR()
  }
})
</script>

<style scoped>
.qrcode-tool {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.generator-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 20px;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.option-item .el-slider {
  flex: 1;
}

.preview-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.qrcode-wrapper {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.button-group {
  margin-top: 20px;
}

.info-section {
  margin-top: 40px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

.ml-20 {
  margin-left: 20px;
}

@media (max-width: 768px) {
  .generator-container {
    grid-template-columns: 1fr;
  }
}
</style> 